import {
  AutoForm,
  AutoFormDataField,
  AutoFormDataFieldDate,
  DataFieldGroup,
  pickerCurrentTime,
  useAutoFormControls
} from "@9amhealth/shared";
import type { ZonedDateTime } from "@internationalized/date";
import type { FC } from "react";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { IconLockInput } from "src/constants/icons";
import leftPadNumber from "src/lib/leftPadNumber";
import translate from "src/lib/translate";
import { validateDoseDate } from "src/lib/zod/validateDate";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { z } from "zod";

const MedicationDoseDateField: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const medicationName = props.field.properties?.input_label ?? "";
  const [, { saveValue }] = useQuestionnaireCubitInContext();
  const { setValue: setFormValue } = useFormContext();

  //#region Schema and types
  const MedicationDoseDateSchema = useMemo(
    () =>
      z.object({
        medication: z.string().min(1),
        date: validateDoseDate(
          pickerCurrentTime() // max date: today
        )
      }),
    []
  );

  type FormValues = z.infer<typeof MedicationDoseDateSchema>;
  //#endregion

  //#region Form initial values and controls
  const initialFormValues = useMemo<FormValues>(() => {
    return {
      medication: medicationName,
      date: pickerCurrentTime()
    };
  }, [medicationName]);

  const formatDate = (date: ZonedDateTime) => {
    return `${date.year}-${leftPadNumber(date.month)}-${leftPadNumber(date.day)}`;
  };

  // set initial form value to ensure the initial date is in the questionnaire context
  useEffect(() => {
    const dateString = formatDate(initialFormValues.date);

    saveValue(props.field.id, dateString);
    setFormValue(props.field.id, dateString);
  }, []);

  const autoFormControls = useAutoFormControls({
    schema: MedicationDoseDateSchema,
    initialValue: initialFormValues,
    validateOnChange: true,
    onChange: (key, value, { errors }) => {
      // Sync AutoForm changes to questionnaire form only if valid
      if (key === "date" && value) {
        const dateTime = value as ZonedDateTime;
        const dateString = formatDate(dateTime);

        // Check if there are any validation errors for the date field
        const hasDateErrors = errors?.date && errors.date.length > 0;

        if (!hasDateErrors) {
          saveValue(props.field.id, dateString, { triggerAutoContinue: true });
        } else {
          // Clear the value if invalid to prevent Continue button from being enabled
          saveValue(props.field.id, undefined, { triggerAutoContinue: false });
        }

        setFormValue(props.field.id, dateString);
      }
    }
  });
  //#endregion

  return (
    <AutoForm {...autoFormControls.props}>
      <DataFieldGroup>
        <AutoFormDataField
          name="medication"
          label={translate("glpContinuation.field.label.medication")}
          affix={IconLockInput()}
          isReadOnly
        />

        <AutoFormDataFieldDate
          maxValue={pickerCurrentTime()}
          label={
            props.field.properties?.input_placeholder ??
            translate("glpContinuation.field.label.recentDoseDate", {
              context: "application"
            })
          }
          name="date"
          granularity="day"
        />
      </DataFieldGroup>
    </AutoForm>
  );
};

export default MedicationDoseDateField;
